import { formatCents, parseMoneyToCents, pickZone, tt } from '@agos/shared'

/**
 * Which zone an address falls in, what delivery costs there, and what it has to be worth.
 *
 * **The arithmetic is the server's, not a better one** — FB-C10. `DeliveryZoneService.withinRadius`
 * measures distance by treating a degree as 111 320 m in both directions, and says why keeping it
 * matters: *"the zones in production were drawn on a map against this arithmetic. A radius the owner
 * dragged until it covered the right streets covers different streets under a correct haversine."*
 *
 * At Bánov's latitude a degree of longitude is 74 412 m, so a "circle" is really an ellipse two thirds
 * as wide as it is tall. Legacy's browser uses haversine instead and accepts a third more area than the
 * server does — 18 997 of 124 000 sampled points, none the other way — which is where the zone minimum
 * quietly stops being enforced, because `requireMinimumOrder` returns without throwing when it finds no
 * zone.
 *
 * So this file reproduces the server, deliberately and including its distortion. The place to fix the
 * geometry is one migration that redraws the zones, not two clients disagreeing.
 */

const METRES_PER_DEGREE = 111_320

export interface ZoneTier {
  orderFrom: number
  orderTo: number | null
  deliveryFee: number
}

export interface DeliveryZone {
  id: string
  name: string
  enabled: boolean
  type: string
  center: { lat: number; lng: number } | null
  radius: number | null
  polygon: { lat: number; lng: number }[] | null
  minOrder: number
  tiers: ZoneTier[]
}

export type ZoneOutcome =
  | { kind: 'none-configured' }
  /**
   * §9.51 — nobody has given an address yet.
   *
   * This used to answer `outside`, so *we have not been told where you live* and *we do not deliver
   * where you live* were one value and printed one sentence. A checkout that refuses an address
   * before it has been given one is accusing somebody of living somewhere before asking.
   */
  | { kind: 'unknown' }
  | { kind: 'outside' }
  | { kind: 'below-minimum'; zone: DeliveryZone; minimumCents: number }
  | { kind: 'ok'; zone: DeliveryZone; feeCents: number }

export function zonesOf(payload: unknown): DeliveryZone[] {
  if (!Array.isArray(payload)) return []
  return payload.map((entry) => {
    const row = entry as Record<string, unknown>
    const centre = row.center as { lat?: unknown; lng?: unknown } | null | undefined
    return {
      id: typeof row.id === 'string' ? row.id : '',
      name: typeof row.name === 'string' ? row.name : '',
      enabled: row.enabled !== false,
      type: typeof row.type === 'string' ? row.type : '',
      center:
        typeof centre?.lat === 'number' && typeof centre.lng === 'number'
          ? { lat: centre.lat, lng: centre.lng }
          : null,
      radius: typeof row.radius === 'number' ? row.radius : null,
      polygon: Array.isArray(row.polygon)
        ? (row.polygon as { lat: number; lng: number }[])
        : null,
      minOrder: typeof row.minOrder === 'number' ? row.minOrder : 0,
      tiers: Array.isArray(row.tiers) ? (row.tiers as ZoneTier[]) : [],
    }
  })
}

/** `withinRadius`, distortion included. */
function withinRadius(zone: DeliveryZone, lat: number, lng: number): boolean {
  if (zone.center === null || zone.radius === null) return false
  const deltaLat = lat - zone.center.lat
  const deltaLng = lng - zone.center.lng
  return Math.sqrt(deltaLat * deltaLat + deltaLng * deltaLng) * METRES_PER_DEGREE <= zone.radius
}

/** `withinPolygon` — ray casting, with the server's strict `<` on the crossing. */
function withinPolygon(zone: DeliveryZone, lat: number, lng: number): boolean {
  const corners = zone.polygon ?? []
  if (corners.length < 3) return false
  let inside = false
  for (let current = 0, previous = corners.length - 1; current < corners.length; previous = current++) {
    const here = corners[current]!
    const there = corners[previous]!
    const straddles = here.lat > lat !== there.lat > lat
    if (straddles) {
      const crossing =
        ((there.lng - here.lng) * (lat - here.lat)) / (there.lat - here.lat) + here.lng
      if (lng < crossing) inside = !inside
    }
  }
  return inside
}

/**
 * The first enabled zone containing the point, in the order the endpoint returned them.
 *
 * The order is load-bearing where zones overlap — 8 of the 465 configured pairs do. `zoneContaining`
 * takes `findAllByOrderByCreatedAtAsc().filter(enabled).findFirst()`, and `GET /delivery-zones` lists by
 * the same query, so the array as received *is* the server's resolution order. Re-sorting it here by
 * name or size would pick a different zone, and a different fee, from the same address.
 */
/**
 * The address to resolve on arrival, or null when there is nothing to do - §9.371, FB-659.
 *
 * **A returning customer's default address is in the field and has no coordinates behind it.**
 * `customerAddress` is `typedAddress ?? knownAddress`, and only the typed half ever went through
 * `AddressField`, whose `onResolved` is what sets `point` - so the commonest checkout there is
 * arrived with an address nobody had resolved and a fee that could not be quoted. The owner's
 * words: *"when I go checkout in customer and I have preselected or select some, it is not actually
 * verified, and asking for Doručovacia zóna."*
 *
 * **Each guard is a bug it would otherwise be**, which is why this is a function rather than three
 * conditions in an effect:
 *
 *   - **a point already exists** - a resolved point is better than a fresh lookup, and replacing it
 *     would move a quote somebody has already read
 *   - **they have typed** - from then on the field owns the address and its own `onResolved`
 *     answers for it, including §9.372's *it did not resolve*
 *   - **the address is blank** - there is nothing to ask about
 */
export function addressToResolve(state: {
  point: { lat: number; lng: number } | null
  typedAddress: string | null
  knownAddress: string
}): string | null {
  if (state.point !== null) return null
  if (state.typedAddress !== null) return null
  const address = state.knownAddress.trim()
  return address === '' ? null : address
}

export function zoneFor(
  lat: number,
  lng: number,
  zones: readonly DeliveryZone[],
  /** §10A.121 - what the customer typed, and where the depot is. Both optional; see `pickZone`. */
  options?: { address?: string | null; depot?: { lat: number; lng: number } | null },
): DeliveryZone | null {
  const containing = zones.filter(
    (zone) =>
      zone.enabled &&
      ((zone.type === 'circle' && withinRadius(zone, lat, lng)) ||
        (zone.type === 'polygon' && withinPolygon(zone, lat, lng))),
  )
  return pickZone(containing, options?.address ?? null, options?.depot ?? null)
}

/*
 * `fold`, `metresToCentre` and `pickZone` moved to `@agos/shared` on 2026-09-17, because §9.346 gave
 * the manager's phone-order picker the same tie to resolve and two copies of this rule would be two
 * delivery fees. Re-exported so every import of it from this module still resolves.
 */
export { pickZone } from '@agos/shared'

/**
 * The tier's fee for a basket, in cents.
 *
 * Tiers are sorted by `orderFrom` and matched half-open — `>= from` and `< to` — which is legacy's rule
 * and the one the configured data was entered against: `orderTo: 19.99` next to `orderFrom: 20`. A
 * basket past every tier takes the last one rather than falling through to free delivery.
 */
export function feeCentsFor(zone: DeliveryZone, subtotalCents: number): number {
  const sorted = [...zone.tiers].sort((left, right) => left.orderFrom - right.orderFrom)
  for (const tier of sorted) {
    const from = parseMoneyToCents(tier.orderFrom)
    const to = tier.orderTo === null ? null : parseMoneyToCents(tier.orderTo)
    if (subtotalCents >= from && (to === null || subtotalCents < to)) {
      return parseMoneyToCents(tier.deliveryFee)
    }
  }
  const last = sorted[sorted.length - 1]
  return last === undefined ? 0 : parseMoneyToCents(last.deliveryFee)
}

/**
 * What delivery to this point costs, or why it cannot be worked out.
 *
 * `none-configured` is its own answer rather than a fee of zero. Legacy charges a hardcoded fallback the
 * moment its fetch completes, and shipped a bug where that fallback was added while the zones were still
 * loading — *"customers saw €10.40 for an €8.50 cart with no address selected."* Not knowing is not the
 * same as free, and it is not the same as 3,50 € either.
 */
export function deliveryFor(
  point: { lat: number; lng: number } | null,
  zones: readonly DeliveryZone[],
  subtotalCents: number,
  /**
   * §10A.121 - what the customer typed, and where the depot is.
   *
   * Both are what decide an address that falls in more than one zone, and both have to match what
   * the server uses or the fee quoted here is not the fee charged. The address is the customer's own
   * text; the depot comes from `GET /delivery-zones/depot`, which exists so the two sides read the
   * same point rather than each geocoding it.
   */
  options?: { address?: string | null; depot?: { lat: number; lng: number } | null },
): ZoneOutcome {
  if (zones.length === 0) return { kind: 'none-configured' }
  if (point === null) return { kind: 'unknown' }
  const zone = zoneFor(point.lat, point.lng, zones, options)
  if (zone === null) return { kind: 'outside' }
  const minimumCents = parseMoneyToCents(zone.minOrder)
  if (minimumCents > 0 && subtotalCents < minimumCents) {
    return { kind: 'below-minimum', zone, minimumCents }
  }
  return { kind: 'ok', zone, feeCents: feeCentsFor(zone, subtotalCents) }
}

/**
 * The next tier worth reaching, and what it takes.
 *
 * Only a **cheaper** one counts: tiers rise as well as fall, and telling a customer they are 4 € away
 * from a dearer delivery is not an incentive.
 */
export function nextCheaperTier(
  zone: DeliveryZone,
  subtotalCents: number,
): { missingCents: number; feeCents: number } | null {
  const sorted = [...zone.tiers].sort((left, right) => left.orderFrom - right.orderFrom)
  const current = feeCentsFor(zone, subtotalCents)
  for (const tier of sorted) {
    const from = parseMoneyToCents(tier.orderFrom)
    const fee = parseMoneyToCents(tier.deliveryFee)
    if (from > subtotalCents && fee < current) {
      return { missingCents: from - subtotalCents, feeCents: fee }
    }
  }
  return null
}

/** Said in place of a fee, when there is no fee to say. */
export function deliveryNote(outcome: ZoneOutcome): string {
  switch (outcome.kind) {
    case 'none-configured':
      return tt('Cenu doručenia vám povieme pri potvrdení objednávky.')
    case 'unknown':
      // Nothing to say yet — the fee follows the address, and asking for it is the field above.
      return ''
    case 'outside':
      return tt('Na túto adresu zatiaľ nedoručujeme.')
    case 'below-minimum':
      return tt('Objednávka nedosahuje minimum pre doručenie do tejto oblasti.')
    default:
      return ''
  }
}

/**
 * What the basket is still short of — `FRONTEND.md` §9.64.
 *
 * The owner asked for a free-delivery bar. Measured on this franchise: of **31** enabled zones,
 * **one** ever reaches a zero fee. So the bar says whichever of three things is true rather than
 * promising something 30 zones do not offer.
 *
 * `null` when there is nothing to reach — a bar that is always on screen showing a full line is
 * furniture, and one that appears when there is something to do is information.
 */
export type BasketProgress =
  | { kind: 'minimum'; missingCents: number; targetCents: number; reachedCents: number }
  | {
      kind: 'cheaper-delivery'
      missingCents: number
      targetCents: number
      reachedCents: number
      feeCents: number
      free: boolean
    }

export function basketProgress(
  outcome: ZoneOutcome,
  subtotalCents: number,
): BasketProgress | null {
  // Below the minimum is a **gate**, not a saving: the order cannot be placed at all, so it comes
  // first however tempting the tier ahead of it is.
  if (outcome.kind === 'below-minimum') {
    return {
      kind: 'minimum',
      missingCents: Math.max(0, outcome.minimumCents - subtotalCents),
      targetCents: outcome.minimumCents,
      reachedCents: Math.min(subtotalCents, outcome.minimumCents),
    }
  }
  if (outcome.kind !== 'ok') return null

  const next = nextCheaperTier(outcome.zone, subtotalCents)
  if (next === null) return null

  const targetCents = subtotalCents + next.missingCents
  return {
    kind: 'cheaper-delivery',
    missingCents: next.missingCents,
    targetCents,
    reachedCents: subtotalCents,
    feeCents: next.feeCents,
    // **Only where the tier really is 0.** A customer told delivery becomes free at 20 € who is then
    // charged 3 € has been lied to by a progress bar, and 30 of 31 zones would do exactly that.
    free: next.feeCents === 0,
  }
}

/** How far along, 0–1, for drawing. Clamped, because a basket can exceed its own target. */
export function progressFraction(progress: BasketProgress): number {
  if (progress.targetCents <= 0) return 1
  return Math.min(1, Math.max(0, progress.reachedCents / progress.targetCents))
}

/**
 * The zone line under the address — `FRONTEND.md` §9.67.
 *
 * It said the zone and the fee. The **minimum** was missing, and it is the one number that can stop
 * an order: a customer reading *Bánov — 2,70 €* has been told what delivery costs and not that the
 * basket has to reach 9,00 € before they can have it. Measured across the 31 zones: minimums run from
 * 6,00 € to 20,00 €, so it is never the same number twice and never guessable from another zone.
 *
 * Said as a fact of the zone, always — not only once the basket is short of it. §9.64's progress line
 * appears when there is something to reach; this is the rule, and somebody deciding what to order
 * wants it before they are caught by it rather than after.
 *
 * A zone with no minimum says nothing extra: `min_order` defaults to 0, and *minimum 0,00 €* is a
 * sentence about nothing.
 */
export function zoneLine(zone: DeliveryZone, feeCents: number): string {
  const minimumCents = parseMoneyToCents(zone.minOrder)
  const fee = `${zone.name} - ${formatCents(feeCents)}`
  return minimumCents <= 0
    ? fee
    : `${fee} · ${tt('Minimálna objednávka')} ${formatCents(minimumCents)}`
}
